import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { Info as InfoIcon, Edit as EditIcon, EmojiEvents as TrophyIcon, Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { getExerciseHistoryMetadata, getExerciseHistorySessions, getProgramExercise, updateProgramExerciseRangesById, getWeightSeriesInfo, updateWeightSet } from "../../services/ExerciseHistoryService";

const parseDate = (dateValue) => {
  if (!dateValue) return null;
  // Handle array format from Java LocalDateTime: [year, month, day, hour, minute, second]
  if (Array.isArray(dateValue)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
    return new Date(year, month - 1, day, hour, minute, second);
  }
  // Handle ISO string format
  return new Date(dateValue);
};

const formatDate = (dateValue) => {
  const date = parseDate(dateValue);
  if (!date || isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatShortDate = (dateValue) => {
  const date = parseDate(dateValue);
  if (!date || isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

const getModeLabel = (mode) => {
  switch (mode) {
    case 'STATIC_WEIGHT_SET': return 'סט קבוע';
    case 'STATIC_INCREMENT': return 'תוספת קבועה';
    case 'NONE': return 'ללא';
    default: return mode;
  }
};

const ExerciseHistoryDialog = ({ isOpen, close, exerciseName, exerciseId, exerciseProgramId, traineeId, isCompleted }) => {
  const [historyTab, setHistoryTab] = useState(2); // Start on history tab
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [hasData, setHasData] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Settings state (from program exercise)
  const [settings, setSettings] = useState({
    targetWeight: '',
    targetReps: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [editingTarget, setEditingTarget] = useState(false);

  // Weight series state
  const [weightSeriesInfo, setWeightSeriesInfo] = useState(null);
  const [selectedWeightsSet, setSelectedWeightsSet] = useState(null);
  const [weightSeriesSaving, setWeightSeriesSaving] = useState(false);
  const [weightSeriesSaved, setWeightSeriesSaved] = useState(false);

  useEffect(() => {
    if (isOpen && exerciseId && traineeId) {
      // Reset state when dialog opens with new exercise
      setSessions([]);
      setPage(0);
      setHasMore(true);
      setSettingsError(null);
      setSettingsSaved(false);
      setEditingTarget(false);
      fetchData();

      fetchTargetOnly();
      fetchWeightSeriesInfo();
    }
  }, [isOpen, exerciseId, traineeId, exerciseProgramId]);

  // Fetch target settings (weight/reps goal)
  const fetchTargetOnly = async () => {
    setSettingsLoading(true);
    try {
      const res = await getProgramExercise(traineeId, exerciseId);
      // res.data is null when the trainee has no current target for this exercise — normal when
      // viewing a past program, since the server resolves targets from the ACTIVE program only.
      // Clear the fields rather than leaving a previous exercise's values on screen.
      setSettings({
        targetWeight: res.data?.targetWeight ?? '',
        targetReps: res.data?.targetReps ?? ''
      });
    } catch (error) {
      console.error('Error fetching exercise target:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchWeightSeriesInfo = async () => {
    try {
      const res = await getWeightSeriesInfo(traineeId, exerciseId);
      if (res.data) {
        setWeightSeriesInfo(res.data);
        setSelectedWeightsSet(res.data.currentWeightsSet);
      }
    } catch (error) {
      console.error('Error fetching weight series info:', error);
    }
  };

  const handleSaveWeightSet = async () => {
    if (!selectedWeightsSet) return;
    setWeightSeriesSaving(true);
    try {
      await updateWeightSet(traineeId, exerciseId, selectedWeightsSet);
      setWeightSeriesSaved(true);
      setWeightSeriesInfo(prev => ({ ...prev, currentWeightsSet: selectedWeightsSet }));
      setTimeout(() => setWeightSeriesSaved(false), 3000);
    } catch (error) {
      console.error('Error saving weight set:', error);
    } finally {
      setWeightSeriesSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setSettingsError(null);
    setSettingsSaved(false);
    try {
      // Use exerciseProgramId if available (updates the specific week's exercise)
      // Otherwise fall back to the old API (updates by traineeId + exerciseId)
      if (exerciseProgramId) {
        await updateProgramExerciseRangesById(exerciseProgramId, {
          targetWeight: settings.targetWeight ? parseFloat(settings.targetWeight) : null,
          targetReps: settings.targetReps ? parseInt(settings.targetReps) : null
        });
      } else {
        // Fallback - should not happen if dialog is opened from WeekRow
        console.warn('ExerciseHistoryDialog: No exerciseProgramId provided, cannot save target');
        setSettingsError('לא ניתן לשמור יעד - חסר מזהה תרגיל');
        setSettingsSaving(false);
        return;
      }
      setSettingsSaved(true);
      setEditingTarget(false);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (error) {
      console.error('Error saving exercise settings:', error);
      setSettingsError('שגיאה בשמירת ההגדרות');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Calculate progress percentage for target based on volume (weight × reps)
  const calculateProgress = () => {
    if (!settings.targetWeight || !settings.targetReps) return 0;
    if (!metadata?.bestVolume?.volume) return 0;

    const targetVolume = settings.targetWeight * settings.targetReps;
    const bestVolume = metadata.bestVolume.volume;
    const progress = (bestVolume / targetVolume) * 100;
    return Math.min(progress, 100);
  };

  // Get the target volume
  const getTargetVolume = () => {
    if (!settings.targetWeight || !settings.targetReps) return 0;
    return settings.targetWeight * settings.targetReps;
  };

  // Get remaining volume to target
  const getRemainingVolume = () => {
    const targetVolume = getTargetVolume();
    const bestVolume = metadata?.bestVolume?.volume || 0;
    return Math.max(0, targetVolume - bestVolume);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [metadataRes, sessionsRes] = await Promise.all([
        getExerciseHistoryMetadata(traineeId, exerciseId),
        getExerciseHistorySessions(traineeId, exerciseId, 0, 20)
      ]);

      setMetadata(metadataRes.data);
      setSessions(sessionsRes.data.content || []);
      setHasData(sessionsRes.data.content?.length > 0);
      setHasMore(!sessionsRes.data.last);
      setPage(0);
    } catch (error) {
      console.error('Error fetching exercise history:', error);
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreSessions = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const sessionsRes = await getExerciseHistorySessions(traineeId, exerciseId, nextPage, 20);
      setSessions(prev => [...prev, ...(sessionsRes.data.content || [])]);
      setHasMore(!sessionsRes.data.last);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more sessions:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when user scrolls near bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreSessions();
    }
  };

  // Transform volumeGraph data for the chart
  const progressData = metadata?.volumeGraph?.map(point => ({
    date: formatShortDate(point.date),
    volume: Math.round(point.totalVolume || 0),
    maxWeight: point.maxWeight || 0,
    totalReps: point.totalReps || 0
  })) || [];

  // Custom tooltip component for the volume graph
  const VolumeTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box sx={{
          backgroundColor: 'white',
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          direction: 'rtl',
          minWidth: '140px'
        }}>
          <Typography sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#1E293B',
            marginBottom: 1.5,
            borderBottom: '1px solid #E2E8F0',
            paddingBottom: 1
          }}>
            {data.date}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography sx={{ fontSize: '12px', color: '#64748B' }}>נפח:</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#14B8A6' }}>
              {data.volume}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography sx={{ fontSize: '12px', color: '#64748B' }}>משקל מקסי:</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#57837E' }}>
              {data.maxWeight} ק"ג
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '12px', color: '#64748B' }}>חזרות:</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1E293B' }}>
              {data.totalReps}
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Transform sessions data for history cards
  const historyData = sessions.map(session => ({
    date: formatDate(session.date),
    sets: session.sets?.length || 0,
    volume: Math.round(session.totalVolume || 0),
    maxWeight: session.maxWeight || 0,
    programName: session.programNameHeb || session.programName || '',
    blockIndex: session.blockIndex,
    weekIndex: session.weekIndex,
    workoutIndex: session.workoutIndex,
    intervals: session.sets?.map(set => ({
      set: (set.setIndex || 0) + 1,
      weight: set.weight || 0,
      reps: set.reps || 0,
      rir: set.rir ?? '-'
    })) || []
  }));

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      maxWidth="xl"
      fullWidth
      dir="rtl"
      PaperProps={{ sx: { borderRadius: "12px", height: "90vh", maxHeight: "90vh" } }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #E2E8F0",
        p: 2
      }}>
        <Typography sx={{ fontSize: "19px", fontWeight: 600, color: "#1E293B" }}>
          {exerciseName || "EXERCISE"}
        </Typography>
        <IconButton onClick={close} size="small">
          <Box sx={{ fontSize: "19px", color: "#64748B" }}>✕</Box>
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{
        px: 2,
        py: 1.5,
        backgroundColor: "#F8FAFC"
      }}>
        <Box sx={{
          display: "flex",
          backgroundColor: "#F1F5F9",
          borderRadius: "10px",
          padding: "4px",
          gap: "4px"
        }}>
          {["היסטוריה", "גרף התקדמות", "הגדרות"].map((label, index) => (
            <Box
              key={index}
              onClick={() => setHistoryTab(2 - index)}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 1.5,
                px: 2,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "15px",
                fontWeight: historyTab === (2 - index) ? 600 : 500,
                color: historyTab === (2 - index) ? "#1E293B" : "#64748B",
                backgroundColor: historyTab === (2 - index) ? "#FFFFFF" : "transparent",
                boxShadow: historyTab === (2 - index) ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#1E293B",
                  backgroundColor: historyTab === (2 - index) ? "#FFFFFF" : "rgba(255,255,255,0.5)"
                }
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, minHeight: "500px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
            <CircularProgress sx={{ color: "#57837E" }} />
          </Box>
        ) : (
          <>
            {/* Tab 0: Settings - Always accessible */}
            {historyTab === 0 && (
              <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
                {settingsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                    <CircularProgress sx={{ color: "#57837E" }} />
                  </Box>
                ) : (
                  <>
                    {/* Target Card */}
                    <Box sx={{ mb: 2 }}>
                      {/* מטרה לתרגיל (Exercise Target) */}
                      <Box sx={{
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        p: 2.5,
                        border: "1px solid #E2E8F0",
                        position: "relative"
                      }}>
                        {/* Edit/Save/Cancel buttons */}
                        {editingTarget ? (
                          <Box sx={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => setEditingTarget(false)}
                              sx={{ color: "#EF4444", "&:hover": { backgroundColor: "#FEE2E2" } }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => { handleSaveSettings(); }}
                              sx={{ color: "#22C55E", "&:hover": { backgroundColor: "#DCFCE7" } }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => setEditingTarget(true)}
                            sx={{ position: "absolute", top: 12, left: 12, color: "#94A3B8" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}

                        {/* Header */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1, mb: 2 }}>
                          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1E293B" }}>
                            מטרה לתרגיל
                          </Typography>
                          <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#22C55E" }} />
                          </Box>
                        </Box>

                        {editingTarget ? (
                            /* Edit Mode */
                            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 2 }}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 0.5 }}>חזרות יעד</Typography>
                                <Box
                                  component="input"
                                  type="number"
                                  value={settings.targetReps}
                                  onChange={(e) => setSettings({ ...settings, targetReps: e.target.value })}
                                  sx={{
                                    width: "120px",
                                    py: 1.5,
                                    px: 2,
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    textAlign: "center",
                                    border: "none",
                                    borderRadius: "8px",
                                    backgroundColor: "#F1F5F9",
                                    color: "#1E293B",
                                    outline: "none",
                                    "&:focus": { backgroundColor: "#E2E8F0" }
                                  }}
                                />
                              </Box>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 0.5 }}>משקל יעד (ק"ג)</Typography>
                                <Box
                                    component="input"
                                    type="number"
                                    step="0.5"
                                    value={settings.targetWeight}
                                    onChange={(e) => setSettings({ ...settings, targetWeight: e.target.value })}
                                    sx={{
                                      width: "120px",
                                      py: 1.5,
                                      px: 2,
                                      fontSize: "18px",
                                      fontWeight: 600,
                                      textAlign: "center",
                                      border: "none",
                                      borderRadius: "8px",
                                      backgroundColor: "#F1F5F9",
                                      color: "#1E293B",
                                      outline: "none",
                                      "&:focus": { backgroundColor: "#E2E8F0" }
                                    }}
                                />
                              </Box>
                            </Box>
                        ) : (
                            /* Display Mode */
                            <Box sx={{ textAlign: "center", mb: 2 }}>
                              {settings.targetWeight ? (
                                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.5 }}>
                                    <Typography sx={{ fontSize: "13px", color: "#64748B" }}>חזרות</Typography>
                                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#F97316" }}>
                                      {settings.targetReps || '-'}
                                    </Typography>
                                    <Typography sx={{ fontSize: "18px", color: "#64748B", mx: 0.5 }}>×</Typography>
                                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#F97316" }}>
                                      {settings.targetWeight}
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", color: "#64748B" }}>ק"ג</Typography>
                                    <Typography sx={{ fontSize: "13px", color: "#94A3B8", mr: 1 }}>משקל</Typography>
                                  </Box>
                              ) : (
                                  <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    py: 1
                                  }}>
                                    <Box sx={{
                                      display: "flex",
                                      alignItems: "baseline",
                                      justifyContent: "center",
                                      gap: 0.5,
                                      opacity: 0.4
                                    }}>
                                      <Typography sx={{ fontSize: "13px", color: "#94A3B8" }}>חזרות</Typography>
                                      <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#94A3B8" }}>--</Typography>
                                      <Typography sx={{ fontSize: "18px", color: "#94A3B8", mx: 0.5 }}>×</Typography>
                                      <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#94A3B8" }}>--</Typography>
                                      <Typography sx={{ fontSize: "13px", color: "#94A3B8" }}>ק"ג</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "12px", color: "#94A3B8", mt: 1 }}>
                                      לחץ על העיפרון להגדרת מטרה
                                    </Typography>
                                  </Box>
                              )}
                            </Box>
                        )}

                        {/* Progress bar */}
                        {settings.targetWeight && settings.targetReps && (
                            <>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                                  התקדמות למטרה
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                                  {Math.round(calculateProgress())}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                  variant="determinate"
                                  value={calculateProgress()}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: "#E2E8F0",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: "#F97316",
                                      borderRadius: 4
                                    }
                                  }}
                              />
                              {metadata?.bestVolume?.volume && (
                                  <Typography sx={{ fontSize: "11px", color: "#94A3B8", mt: 0.5, textAlign: "right" }}>
                                    נותרו {Math.round(getRemainingVolume())} נפח עד למטרה
                                  </Typography>
                              )}
                            </>
                        )}
                      </Box>
                    </Box>


                    {/* Weight Series Card */}
                    {weightSeriesInfo && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{
                          backgroundColor: "#FFFFFF",
                          borderRadius: "12px",
                          p: 2.5,
                          border: "1px solid #E2E8F0"
                        }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1, mb: 2 }}>
                            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1E293B" }}>
                              סוג משקולות
                            </Typography>
                            <Chip
                              label={getModeLabel(weightSeriesInfo.mode)}
                              size="small"
                              sx={{ fontSize: "12px", backgroundColor: "#F1F5F9", color: "#64748B" }}
                            />
                          </Box>

                          {weightSeriesInfo.mode === 'STATIC_WEIGHT_SET' && weightSeriesInfo.availableWeightsSets?.length > 0 && (
                            <>
                              <Box sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 1 }}>סט משקולות</Typography>
                                <Select
                                  value={selectedWeightsSet || ''}
                                  onChange={(e) => setSelectedWeightsSet(e.target.value)}
                                  size="small"
                                  fullWidth
                                  sx={{ fontSize: "14px", borderRadius: "8px" }}
                                >
                                  {weightSeriesInfo.availableWeightsSets.map(set => (
                                    <MenuItem key={set} value={set} sx={{ fontSize: "14px" }}>
                                      {set.replace(/_/g, ' ')}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Box>

                              {selectedWeightsSet !== weightSeriesInfo.currentWeightsSet && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleSaveWeightSet}
                                  disabled={weightSeriesSaving}
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "13px",
                                    borderColor: "#57837E",
                                    color: "#57837E",
                                    "&:hover": { borderColor: "#456b67", backgroundColor: "#F0FDF4" }
                                  }}
                                >
                                  {weightSeriesSaving ? <CircularProgress size={16} /> : "שמור סט"}
                                </Button>
                              )}

                              {weightSeriesSaved && (
                                <Typography sx={{ fontSize: "12px", color: "#10B981", mt: 1 }}>
                                  הסט נשמר בהצלחה
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Save Button */}
                    <Box sx={{ display: "flex", justifyContent: "center", mt: "auto", pt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleSaveSettings}
                        disabled={settingsSaving}
                        sx={{
                          backgroundColor: "#57837E",
                          "&:hover": { backgroundColor: "#456b67" },
                          textTransform: "none",
                          px: 4,
                          py: 1.5,
                          borderRadius: "8px",
                          fontSize: "15px",
                          fontWeight: 600,
                          minWidth: "150px"
                        }}
                      >
                        {settingsSaving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "שמור הגדרות"}
                      </Button>
                    </Box>
                    {settingsSaved && (
                      <Typography sx={{ fontSize: "14px", color: "#10B981", textAlign: "center", mt: 1 }}>
                        ההגדרות נשמרו בהצלחה
                      </Typography>
                    )}
                    {settingsError && (
                      <Typography sx={{ fontSize: "14px", color: "#EF4444", textAlign: "center", mt: 1 }}>
                        {settingsError}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Tab 1: Progress Graph */}
            {historyTab === 1 && (
              !hasData ? (
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "400px",
                  gap: 2
                }}>
                  <InfoIcon sx={{ fontSize: "48px", color: "#94A3B8" }} />
                  <Typography sx={{ fontSize: "17px", color: "#64748B" }}>
                    אין היסטוריה לתרגיל זה
                  </Typography>
                </Box>
              ) : (
              <Box sx={{ p: 2, pt: 2 }}>
                <Box sx={{
                  backgroundColor: "#F8FAFC",
                  borderRadius: "12px",
                  p: 2,
                  border: "1px solid #E2E8F0",
                  mb: 2
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B" }}>
                      גרף התקדמות נפח
                    </Typography>
                  </Box>

                  <Box sx={{ height: "280px", direction: "ltr" }}>
                    {progressData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={progressData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                          <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#64748B", fontSize: 13 }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#64748B", fontSize: 13 }}
                          />
                          <RechartsTooltip content={<VolumeTooltip />} />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value) => <span style={{ color: "#64748B", fontSize: "13px" }}>{value}</span>}
                          />
                          <Line
                            type="monotone"
                            dataKey="volume"
                            name="נפח כולל"
                            stroke="#14B8A6"
                            strokeWidth={2}
                            dot={{ fill: "#14B8A6", strokeWidth: 0, r: 5 }}
                            activeDot={{ r: 7 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                        <Typography sx={{ color: "#64748B" }}>אין נתונים להצגה</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Insights Box */}
                {metadata?.bestWeight && (
                  <Box sx={{
                    backgroundColor: "#F8FAFC",
                    borderRadius: "12px",
                    p: 2,
                    border: "1px solid #E2E8F0"
                  }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1E293B", mb: 1, textAlign: "right" }}>
                      תובנות:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pr: 2, color: "#64748B", fontSize: "14px", lineHeight: 1.8 }}>
                      <li>המשקל המקסימלי שהורם: {metadata.bestWeight.weight} ק"ג</li>
                      <li>מספר אימונים: {sessions.length}</li>
                      {metadata.bestVolume && <li>נפח מקסימלי בסט: {Math.round(metadata.bestVolume.volume)}</li>}
                    </Box>
                  </Box>
                )}
              </Box>
              )
            )}

            {/* Tab 2: History */}
            {historyTab === 2 && (
              !hasData ? (
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "400px",
                  gap: 2
                }}>
                  <InfoIcon sx={{ fontSize: "48px", color: "#94A3B8" }} />
                  <Typography sx={{ fontSize: "17px", color: "#64748B" }}>
                    אין היסטוריה לתרגיל זה
                  </Typography>
                </Box>
              ) : (
              <Box sx={{ p: 2, pt: 2, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Top Sets Banner - Best Weight & Best Volume */}
                {(metadata?.bestWeight || metadata?.bestVolume) && (
                  <Box sx={{
                    display: "grid",
                    gridTemplateColumns: metadata?.bestWeight && metadata?.bestVolume ? "1fr 1fr" : "1fr",
                    gap: 2,
                    mb: 1.5
                  }}>
                    {/* Best Weight */}
                    {metadata?.bestWeight && (
                      <Box sx={{
                        backgroundColor: "#F8FAFC",
                        borderRadius: "10px",
                        py: 1,
                        px: 2,
                        border: "1px solid #E2E8F0",
                        textAlign: "center"
                      }}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
                            טופ סט - מקסימום משקל
                          </Typography>
                          <TrophyIcon sx={{ fontSize: "17px", color: "#F59E0B" }} />
                        </Box>
                        <Typography sx={{ fontSize: "13px", color: "#94A3B8", mb: 0.5 }}>
                          {formatDate(metadata.bestWeight.date)}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 0.5 }}>

                          <Typography sx={{ fontSize: "13px", color: "#64748B" }}>ק"ג</Typography>
                          <Typography sx={{ fontSize: "23px", fontWeight: 700, color: "#57837E" }}>
                            {metadata.bestWeight.weight}
                          </Typography>
                          <Typography sx={{ fontSize: "17px", color: "#64748B" }}>×</Typography>
                          <Typography sx={{ fontSize: "23px", fontWeight: 700, color: "#1E293B" }}>
                            {metadata.bestWeight.reps}
                          </Typography>
                          <Typography sx={{ fontSize: "13px", color: "#64748B" }}>חזרות</Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Best Volume */}
                    {metadata?.bestVolume && (
                      <Box sx={{
                        backgroundColor: "#F8FAFC",
                        borderRadius: "10px",
                        py: 1,
                        px: 2,
                        border: "1px solid #E2E8F0",
                        textAlign: "center"
                      }}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
                            טופ סט - מקסימום נפח
                          </Typography>
                          <TrophyIcon sx={{ fontSize: "17px", color: "#14B8A6" }} />
                        </Box>
                        <Typography sx={{ fontSize: "13px", color: "#94A3B8", mb: 0.5 }}>
                          {formatDate(metadata.bestVolume.date)}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 0.5 }}>
                          <Typography sx={{ fontSize: "23px", fontWeight: 700, color: "#57837E" }}>
                            {metadata.bestVolume.weight}
                          </Typography>
                          <Typography sx={{ fontSize: "13px", color: "#64748B" }}>ק"ג</Typography>
                          <Typography sx={{ fontSize: "17px", color: "#64748B" }}>×</Typography>
                          <Typography sx={{ fontSize: "23px", fontWeight: 700, color: "#1E293B" }}>
                            {metadata.bestVolume.reps}
                          </Typography>
                          <Typography sx={{ fontSize: "13px", color: "#64748B" }}>חזרות</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                          נפח: {Math.round(metadata.bestVolume.volume)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Workout History Cards */}
                <Box
                  onScroll={handleScroll}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 1.5,
                    mb: 2,
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    pr: 0.5,
                    alignItems: "stretch",
                    alignContent: "start",
                    direction: "ltr" // Make grid flow left-to-right so newest is top-left
                  }}>
                  {historyData.map((workout, idx) => (
                    <Box key={idx} sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #E2E8F0",
                      fontSize: "13px",
                      display: "flex",
                      flexDirection: "column",
                      direction: "rtl" // Keep card content RTL for Hebrew text
                    }}>
                      {/* Colored header section */}
                      <Box sx={{
                        backgroundColor: "#F8FAFC",
                        borderBottom: "1px solid #E2E8F0",
                        p: 1.5
                      }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B", textAlign: "center", mb: 0.5 }}>
                          {workout.date}
                        </Typography>
                        {workout.programName && (
                          <Typography sx={{ fontSize: "11px", color: "#57837E", textAlign: "center", mb: 0.5, fontWeight: 500 }}>
                            {workout.programName}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: "11px", color: "#94A3B8", textAlign: "center", mb: 1 }}>
                          בלוק {(workout.blockIndex ?? 0) + 1} | שבוע {(workout.weekIndex ?? 0) + 1} | אימון {(workout.workoutIndex ?? 0) + 1}
                        </Typography>
                        {/*<Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>*/}
                        {/*  <Typography sx={{ fontSize: "12px", color: "#64748B" }}>{workout.sets} סטים</Typography>*/}
                        {/*</Box>*/}
                        <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B" }}>נפח</Typography>
                            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#57837E" }}>{workout.volume}</Typography>
                          </Box>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B" }}>מקסי</Typography>
                            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B" }}>
                              {workout.maxWeight} <span style={{ fontSize: "12px", fontWeight: 400, color: "#64748B" }}>ק"ג</span>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Sets table */}
                      <Box sx={{ px: 1, pb: 1 }}>
                        <Table size="small" sx={{ '& td, & th': { border: 0, p: 0.3, fontSize: "12px" } }}>
                          <TableHead>
                            <TableRow>
                              <TableCell align="center" sx={{ color: "#94A3B8", fontWeight: 500 }}>סט</TableCell>
                              <TableCell align="center" sx={{ color: "#94A3B8", fontWeight: 500 }}>משקל</TableCell>
                              <TableCell align="center" sx={{ color: "#94A3B8", fontWeight: 500 }}>חזרות</TableCell>
                              <TableCell align="center" sx={{ color: "#94A3B8", fontWeight: 500 }}>RIR</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {workout.intervals.map((interval, iIdx) => (
                              <TableRow key={iIdx}>
                                <TableCell align="center" sx={{ color: "#64748B" }}>{interval.set}#</TableCell>
                                <TableCell align="center" sx={{ color: "#57837E", fontWeight: 500 }}>{interval.weight} <span style={{ fontSize: "10px", fontWeight: 400, color: "#94A3B8" }}>ק"ג</span></TableCell>
                                <TableCell align="center" sx={{ color: "#1E293B" }}>{interval.reps}</TableCell>
                                <TableCell align="center" sx={{ color: interval.rir === 0 ? "#EF4444" : "#64748B", fontWeight: interval.rir === 0 ? 600 : 400 }}>
                                  {interval.rir}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  ))}
                  {/* Loading indicator for infinite scroll */}
                  {loadingMore && (
                    <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} sx={{ color: "#57837E" }} />
                    </Box>
                  )}
                </Box>

                {/* Summary Stats */}
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                  backgroundColor: "#F8FAFC",
                  borderRadius: "12px",
                  p: 2,
                  border: "1px solid #E2E8F0"
                }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 0.5 }}>אימונים בסה"כ</Typography>
                    <Typography sx={{ fontSize: "25px", fontWeight: 700, color: "#1E293B" }}>{sessions.length}</Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 0.5 }}>משקל מקסימלי</Typography>
                    <Typography sx={{ fontSize: "25px", fontWeight: 700, color: "#57837E" }}>
                      {metadata?.bestWeight?.weight || 0} ק"ג
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 0.5 }}>נפח מקסימלי</Typography>
                    <Typography sx={{ fontSize: "25px", fontWeight: 700, color: "#1E293B" }}>
                      {metadata?.bestVolume ? Math.round(metadata.bestVolume.volume) : 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              )
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExerciseHistoryDialog;
